function localDateStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function computeRange(preset) {
    const today = new Date();
    const todayStr = localDateStr(today);
    const currentYear = today.getFullYear();

    switch (preset) {
        case 'last-year': {
            const d = new Date(currentYear, today.getMonth() - 11, 1);
            return {from: localDateStr(d), to: todayStr};
        }
        case 'all-time': {
            return {from: null, to: null};
        }
        default: {
            if (preset?.startsWith('year-')) {
                const y = Number.parseInt(preset.slice(5), 10);
                return {from: `${y}-01-01`, to: y >= currentYear ? todayStr : `${y}-12-31`};
            }
            return {from: `${currentYear}-01-01`, to: todayStr};
        }
    }
}

export function availableYears(accounts = []) {
    const currentYear = new Date().getFullYear();
    const openedYears = accounts.map(a => a.openedDate ? Number.parseInt(a.openedDate.slice(0, 4), 10) : currentYear);
    const earliest = openedYears.length > 0 ? Math.min(...openedYears) : currentYear;
    return Array.from({length: currentYear - earliest + 1}, (_, i) => currentYear - i);
}

export function buildMonthsFromSummaries(summaries) {
    const now = new Date();

    if (!summaries || summaries.length === 0) {
        return buildMonths(localDateStr(now), localDateStr(now));
    }

    let max = null;
    let min = null;

    for (const sum of summaries) {
        let summaryDate = new Date(sum.month + "-01T00:00:00.000Z");

        if (!min || summaryDate.getTime() < min.getTime()) {
            min = summaryDate;
        }

        if (!max || summaryDate.getTime() > max.getTime()) {
            max = summaryDate;
        }
    }

    return buildMonths(localDateStr(min ?? now), localDateStr(max ?? now));
}

export function buildMonths(from, to) {
    const months = [];
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    const pad = n => String(n).padStart(2, '0');

    let year = fy, month = fm;
    while (year < ty || (year === ty && month <= tm)) {
        const lastDay = new Date(year, month, 0).getDate();
        months.push({
            label: new Date(year, month - 1, 1).toLocaleString('default', {month: 'short', year: '2-digit'}),
            endDate: `${year}-${pad(month)}-${lastDay}`,
        });
        month++;
        if (month > 12) {month = 1; year++;}
    }
    return months;
}

/** @returns {{ [currency]: {income: number, net: number, expense: number, transferIn: number}[] }} */
export function summariesToEventData(summaries, months) {
    const result = {};
    summaries.forEach(({month, currency, income, net, expense, transferIn}) => {
        if (!result[currency]) {
            result[currency] = months.map(() => ({income: 0, net: 0, expense: 0, transferIn: 0}));
        }
        const idx = months.findIndex(m => m.endDate.slice(0, 7) === month);
        if (idx === -1) return;
        result[currency][idx].income     = Number(income     ?? 0);
        result[currency][idx].net        = Number(net        ?? 0);
        result[currency][idx].expense    = Number(expense    ?? 0);
        result[currency][idx].transferIn = Number(transferIn ?? 0);
    });
    return result;
}

/** @returns {{ [currency]: number[] }} */
export function aggregateBalances(timelines, months) {
    const result = {};
    timelines.forEach(({currencyCode, balancePoints}) => {
        if (!result[currencyCode]) result[currencyCode] = months.map(() => 0);
        months.forEach((month, idx) => {
            const relevant = (balancePoints ?? []).filter(p => p.date <= month.endDate);
            if (!relevant.length) return;
            result[currencyCode][idx] += Number(relevant[relevant.length - 1].balance);
        });
    });
    return result;
}

export function getActiveCurrencies(eventData, balanceData) {
    return [...new Set([...Object.keys(eventData), ...Object.keys(balanceData)])].sort();
}

// ── Historical exchange rates ────────────────────────────────────────

/**
 * For each month, finds the most recent CurrencyRate with date ≤ month end.
 *
 * @param {{label, endDate}[]} months
 * @param {object[]} currencyRates
 * @returns {{ [currency]: number[] }}  e.g. { USD: [41, 41, 42, ...], EUR: [44, ...], UAH: [1, ...] }
 */
export function computeMonthlyRates(months, currencyRates) {
    const uahRates = {UAH: months.map(() => 1)};

    if (!currencyRates?.length) return uahRates;

    const sorted = [...currencyRates].sort((a, b) => a.date.localeCompare(b.date));

    uahRates.USD = months.map(() => 1);
    uahRates.EUR = months.map(() => 1);

    months.forEach((month, i) => {
        const applicable = sorted.reduce(
            (found, r) => (r.date <= month.endDate ? r : found),
            null
        );
        if (!applicable) return;
        uahRates.USD[i] = Number(applicable.uahToUsdRate) || 1;
        uahRates.EUR[i] = Number(applicable.uahToEurRate) || 1;
    });

    return uahRates;
}

// ── Chart data builders ─────────────────────────────────────────────

const TOOLTIP_COL = {role: 'tooltip', type: 'string'};

function fmt(n) {
    return Math.round(n).toLocaleString();
}

/**
 * Builds a tooltip string showing per-currency amounts and their UAH equivalents.
 * Uses the historically-accurate rate for the given month index.
 */
function buildAllTooltip(amountByCurrency, monthlyRates, monthIdx) {
    const parts = [];
    let totalUah = 0;

    Object.entries(amountByCurrency).forEach(([currency, amount]) => {
        if (!amount) return;
        const rate = monthlyRates[currency]?.[monthIdx] ?? 1;
        const inUah = amount * rate;
        totalUah += inUah;
        const suffix = currency === 'UAH' ? '' : ` (≈${fmt(inUah)} UAH)`;
        parts.push(`${fmt(amount)} ${currency}${suffix}`);
    });

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return parts.join('\n') + `\nTotal: ${fmt(totalUah)} UAH`;
}

/**
 * Combined Income + Expense chart data. Expense bar height = expenses + taxes combined.
 * Tooltip shows expenses and taxes as separate line items.
 *
 * @param {'ALL'|string} currencyMode
 * @param {{label, endDate}[]} months
 * @param {{ [currency]: {income, net, expense}[] }} eventData
 * @param {{ [currency]: number[] }} monthlyRates
 */
export function buildIncomeExpenseChartData(currencyMode, months, eventData, monthlyRates) {
    const header = ['Month', 'Income', TOOLTIP_COL, 'Expense', TOOLTIP_COL];

    if (currencyMode === 'ALL') {
        return [header, ...months.map((m, i) => {
            const incomeByCur = {}, expenseByCur = {}, taxByCur = {};

            Object.entries(eventData).forEach(([c, data]) => {
                const inc = data[i]?.income ?? 0;
                const exp = data[i]?.expense ?? 0;
                const tax = Math.max(0, inc - (data[i]?.net ?? 0));
                if (inc) incomeByCur[c]  = inc;
                if (exp) expenseByCur[c] = exp;
                if (tax) taxByCur[c]     = tax;
            });

            const totalIncome  = Object.entries(incomeByCur) .reduce((s, [c, v]) => s + v * (monthlyRates[c]?.[i] ?? 1), 0);
            const totalExpense = Object.entries(expenseByCur).reduce((s, [c, v]) => s + v * (monthlyRates[c]?.[i] ?? 1), 0);
            const totalTax     = Object.entries(taxByCur)    .reduce((s, [c, v]) => s + v * (monthlyRates[c]?.[i] ?? 1), 0);
            const totalCost    = totalExpense + totalTax;

            const expPart = buildAllTooltip(expenseByCur, monthlyRates, i);
            const taxPart = buildAllTooltip(taxByCur, monthlyRates, i);
            const costTooltip = [
                expPart && `Expenses:\n${expPart}`,
                taxPart && `Tax:\n${taxPart}`,
            ].filter(Boolean).join('\n');

            return [
                m.label,
                totalIncome, buildAllTooltip(incomeByCur, monthlyRates, i),
                totalCost,   costTooltip,
            ];
        })];
    }

    const data = eventData[currencyMode];
    return [header, ...months.map((m, i) => {
        const inc = data?.[i]?.income  ?? 0;
        const exp = data?.[i]?.expense ?? 0;
        const tax = Math.max(0, (data?.[i]?.income ?? 0) - (data?.[i]?.net ?? 0));
        const total = exp + tax;
        const tooltip = total
            ? [exp && `Expenses: ${fmt(exp)} ${currencyMode}`, tax && `Tax: ${fmt(tax)} ${currencyMode}`]
                .filter(Boolean).join('\n')
            : '';
        return [
            m.label,
            inc, inc ? `${fmt(inc)} ${currencyMode}` : '',
            total, tooltip,
        ];
    })];
}

/**
 * Balance line chart data with per-month historical rates for "ALL" mode.
 */
export function buildBalanceChartData(currencyMode, months, balanceData, monthlyRates) {
    const header = ['Month', 'Balance', TOOLTIP_COL];

    if (currencyMode === 'ALL') {
        return [header, ...months.map((m, i) => {
            const byCurrency = {};
            Object.entries(balanceData).forEach(([c, values]) => {
                const v = values[i] ?? 0;
                if (v) byCurrency[c] = v;
            });
            const total = Object.entries(byCurrency)
                .reduce((s, [c, v]) => s + v * (monthlyRates[c]?.[i] ?? 1), 0);
            return [m.label, total, buildAllTooltip(byCurrency, monthlyRates, i)];
        })];
    }

    const values = balanceData[currencyMode];
    return [header, ...months.map((m, i) => {
        const v = values?.[i] ?? 0;
        return [m.label, v, v ? `${fmt(v)} ${currencyMode}` : ''];
    })];
}
